/** A code block with a copy button; used for agent connection snippets and compile commands */

import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'

export function CopySnippet({ code, label, className }: { code: string; label: string; className?: string }) {
  const t = useT()
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be blocked (insecure origin, permissions); the text stays selectable
    }
  }

  return (
    <div className={cn('relative', className)}>
      <pre aria-label={label} className="overflow-x-auto whitespace-pre rounded-lg border border-border bg-surface-muted p-3 pr-12 font-mono text-xs leading-relaxed text-fg">
        {code}
      </pre>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={t('connect.copyNamed', { name: label })}
        title={t('connect.copy')}
        className="absolute right-1.5 top-1.5 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-fg-subtle hover:bg-surface-hover hover:text-fg"
      >
        {copied ? <Check className="h-4 w-4 text-success" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
      </button>
      <span role="status" className="sr-only">
        {copied ? t('connect.copied') : ''}
      </span>
    </div>
  )
}
